import { useState, useEffect, useCallback, useRef } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T, debounceMs = 1000) {
  // Get stored value or use initial
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check if draft existed on mount
  useEffect(() => {
    try {
      const item = localStorage.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        // Check if it has meaningful data (at least client name or items)
        if (parsed?.clientName || (parsed?.items && parsed.items.length > 0)) {
          setHasRestoredDraft(true);
        }
      }
    } catch (error) {
      console.error(`Error checking localStorage key "${key}":`, error);
    }
  }, [key]);

  // Debounced save to localStorage
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    setStoredValue(prev => {
      const valueToStore = value instanceof Function ? value(prev) : value;
      
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set new debounced timer
      debounceTimerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
          console.error(`Error saving to localStorage key "${key}":`, error);
        }
      }, debounceMs);
      
      return valueToStore;
    });
  }, [key, debounceMs]);

  // Clear localStorage
  const clearValue = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    try {
      localStorage.removeItem(key);
      setStoredValue(initialValue);
      setHasRestoredDraft(false);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { value: storedValue, setValue, clearValue, hasRestoredDraft };
}

// Utility to check if a draft exists
export function hasDraft(key: string): boolean {
  try {
    const item = localStorage.getItem(key);
    if (!item) return false;
    const parsed = JSON.parse(item);
    // Check if it has meaningful data
    return !!(parsed?.clientName || (parsed?.items && parsed.items.length > 0));
  } catch {
    return false;
  }
}

// Utility to clear a draft
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing draft "${key}":`, error);
  }
}
